import { useCustomAttributeSectionsDefinition } from "#src/batteries-included-components/Panels/FormPanels/EquipmentFormPanel/EquipmentFormPanel.helpers";
import CustomAttributeField from "#src/components/Common/CustomAttributeField";
import {
  useGetOneTemplatedConfiguration,
  useGetTemplatedConfigurationInputSchema,
} from "#src/components/hooks/adapters/useTemplatedConfigurations";
import {
  useMemoizedFormState,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import {
  Accordion,
  Form,
  Panel,
  useForm,
} from "@validereinc/common-components";
import {
  CustomAttributeType,
  EquipmentSchema,
  Resources,
  ResourceType,
} from "@validereinc/domain";
import { TemplatedConfigurations } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useRef } from "react";
import styles from "./AssetDetailsFormStep.module.css";

const cx = classNames.bind(styles);

const STEP_NUMBER = 2;

export type StaticFieldToDisplayType = Pick<
  CustomAttributeType,
  "display_name" | "field_name" | "data_type"
> & {
  pick_list_values?:
    | Array<{
        id: string;
        name: string;
      }>
    | string[];
  is_required?: boolean;
  [key: string]: any;
};

export const AssetDetailsFormStep = ({
  resourceType,
  staticFieldsToDisplay,
  isLoadingFields,
}: {
  resourceType: ResourceType;
  staticFieldsToDisplay: StaticFieldToDisplayType[];
  isLoadingFields?: boolean;
}) => {
  const today = useRef(new Date());

  const { currentStep, updateStep, getStep } = useMultiStepFormContext();
  const isStepActive = currentStep === STEP_NUMBER;
  const prevStep = getStep(1);
  const prevStepValues = prevStep?.getValues();

  const form = useForm();
  const getFormState = useMemoizedFormState(form);

  const isUsingTemplate = !!prevStepValues?.template_name as boolean;

  const templatedConfigurationQuery = useGetOneTemplatedConfiguration(
    {
      id: (prevStepValues?.template_name as string) ?? "",
    },
    {
      enabled: !!prevStepValues?.template_name,
    }
  );

  const templatedConfiguration = templatedConfigurationQuery.data?.data;

  const mainResourceId = useMemo(
    () =>
      TemplatedConfigurations.logic.getResourceIdForResource(
        resourceType,
        templatedConfiguration
      ) ?? "",
    [templatedConfiguration?.display_name]
  );

  const isInputSchemaQueryEnabled = !!prevStepValues?.template_name;

  const inputSchemaQuery = useGetTemplatedConfigurationInputSchema(
    {
      id: (prevStepValues?.template_name as string) ?? "",
      meta: {
        custom_attribute_configuration: {
          ...(resourceType === Resources.EQUIPMENT && mainResourceId
            ? {
                [mainResourceId]: {
                  entity_subtype: (prevStepValues?.type_id as string) ?? "",
                },
              }
            : {}),
          ...(resourceType === Resources.FLOW && mainResourceId
            ? {
                [mainResourceId]: {
                  entity_subtype: (prevStepValues?.type as string) ?? "",
                },
              }
            : {}),
        },
      },
    },
    {
      enabled: isInputSchemaQueryEnabled,
    }
  );

  const inputSchema = inputSchemaQuery.data?.data;

  const customAttributeSectionsDefinition =
    useCustomAttributeSectionsDefinition({
      templatedConfiguration,
      inputSchema,
      primaryResourceType: resourceType,
    });

  const orderedInputSchemaInputs = [
    ...Object.entries(inputSchema?.inputs ?? {}),
  ].sort(([_, valueA], [__, valueB]) => {
    // If order is missing for both values, keep the order as is.
    if (
      typeof valueA.order === "undefined" &&
      typeof valueB.order === "undefined"
    )
      return 0;

    // If there's at least one order value available, one of them can be Infinity.
    const orderA = valueA.order ?? Infinity;
    const orderB = valueB.order ?? Infinity;
    return orderA - orderB;
  });

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues(),
      getFormState,
    });
  }, [getFormState]);

  useEffect(() => {
    if (!mainResourceId) return;
    form.setValue("main_resource_id", mainResourceId);
  }, [mainResourceId]);

  useEffect(() => {
    if (resourceType !== Resources.EQUIPMENT) {
      return;
    }

    form.setValue(
      EquipmentSchema.keyof().Enum.effective_date,
      isUsingTemplate ? undefined : today.current
    );
  }, [isUsingTemplate]);

  const isInputPanelLoaded =
    !isLoadingFields &&
    ((isUsingTemplate &&
      isInputSchemaQueryEnabled &&
      !inputSchemaQuery.isLoading) ||
      !isUsingTemplate);

  if (!isStepActive) return null;

  return (
    <Panel
      className={cx("panel")}
      loaded={isInputPanelLoaded}
    >
      <Form {...form}>
        <h4 className={cx("section-title")}>Details</h4>
        <div className={cx("basic-info-container")}>
          {isUsingTemplate && (
            <>
              {orderedInputSchemaInputs.map(([attributeId, attributeBody]) => (
                <CustomAttributeField
                  key={`inputs.${attributeId}`}
                  name={`inputs.${attributeId}`}
                  attribute={attributeBody as CustomAttributeType}
                  subtype={(prevStepValues?.type_id as string) ?? null}
                />
              ))}
              <Accordion>
                {customAttributeSectionsDefinition.map((section) => {
                  // Show required fields first
                  // Then show optional fields in alphabetical order
                  const sortedFields = Object.entries(section.fields).sort(
                    ([_, fieldA], [__, fieldB]) => {
                      if (!fieldA.is_required && !fieldB.is_required) {
                        return fieldA.display_name.localeCompare(
                          fieldB.display_name
                        );
                      } else {
                        return (
                          Number(fieldB.is_required) -
                          Number(fieldA.is_required)
                        );
                      }
                    }
                  );

                  return (
                    <Accordion.AccordionPanel
                      title={section.title}
                      dataKey={section.resourceId}
                      key={section.resourceId}
                    >
                      {sortedFields.map(([fieldKey, field]) => (
                        <CustomAttributeField
                          key={`custom_attribute_inputs.${section.resourceId}.${fieldKey}`}
                          name={`custom_attribute_inputs.${section.resourceId}.${fieldKey}`}
                          attribute={field}
                          subtype={(prevStepValues?.type_id as string) ?? null}
                        />
                      ))}
                    </Accordion.AccordionPanel>
                  );
                })}
              </Accordion>
            </>
          )}
          {!isUsingTemplate && (
            <>
              {staticFieldsToDisplay.map((field) => {
                return (
                  <CustomAttributeField
                    key={field.field_name}
                    name={field.field_name}
                    attribute={field as any}
                    subtype={(prevStepValues?.type_id as string) ?? null}
                  />
                );
              })}
            </>
          )}
        </div>
      </Form>
    </Panel>
  );
};
